'use client';

import { HStack, Button, Icon, useToast } from '@chakra-ui/react';
import { NestedHome } from '@habity/types';
import { ICONS } from '~/constants';
import { useWishlist } from '~/hooks/useWishlist';
import { getStreet, isAddedToWishlist } from '@habity/services';
import { useEffect, useState } from 'react';
import { useAppStore } from '~/store';
import { useUserData } from '~/hooks/useUserData';

interface HomeProfileLikeAndShareProps {
  home: NestedHome;
}

export function HomeProfileLikeAndShare({
  home,
}: HomeProfileLikeAndShareProps) {
  // Use SWR-managed user data - updates automatically when mutate(['users', user.id]) is called
  const user = useUserData();
  const { addToWishlist, removeFromWishlist, isAddingToWishlist } =
    useWishlist();
  const isMobile = useAppStore((state) => state.isMobile);
  const toast = useToast();
  const [isWebShareSupported, setIsWebShareSupported] =
    useState<boolean>(false);

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsWebShareSupported('share' in navigator);
    }
  }, []);

  // Track loading state for remove action only
  const [isRemovingFromWishlist, setIsRemovingFromWishlist] =
    useState<boolean>(false);

  // Derive wishlist state directly from SWR data
  const isInWishlist = user ? isAddedToWishlist(user, home.id) : false;

  // Handle toggling wishlist status
  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isAddingToWishlist || isRemovingFromWishlist) return;

    if (isInWishlist) {
      // Remove from wishlist - SWR will update state automatically via mutate
      setIsRemovingFromWishlist(true);
      try {
        await removeFromWishlist(home);
      } catch (error) {
        if (!isMobile)
          toast({
            title: 'Något gick fel',
            description: 'Försök igen senare',
            status: 'error',
            position: 'bottom',
          });
      } finally {
        setIsRemovingFromWishlist(false);
      }
    } else {
      // Add to wishlist - hook handles everything (login check, API call, confirmation modal)
      // SWR will update state automatically via mutate(['users', user.id])
      await addToWishlist(home);
    }
  };

  const onShare = async () => {
    if (!isWebShareSupported) return;

    try {
      navigator.share({
        title: 'Erik Olsson Fastighetsförmedling',
        text: getStreet(home),
        url: window.location.href,
      });
    } catch (e) {
      console.error('Share failed', e);
    }
  };

  return (
    <HStack ml="-12px" spacing="0" my="-1.5">
      <Button
        variant="subtle"
        fontSize="sm"
        fontWeight="normal"
        leftIcon={
          <Icon
            as={isInWishlist ? ICONS.WISHLIST_ACTIVE : ICONS.WISHLIST}
            boxSize="24px"
          />
        }
        onClick={handleWishlistToggle}
      >
        Spara
      </Button>

      {isWebShareSupported && (
        <Button
          variant="subtle"
          fontSize="sm"
          fontWeight="normal"
          leftIcon={<Icon as={ICONS.CHAT_INPUT} boxSize="24px" />}
          onClick={onShare}
        >
          Dela
        </Button>
      )}
    </HStack>
  );
}
